import { AxiosRequestConfig, AxiosResponse } from "axios";
import { Dayjs } from "dayjs";
import http from "utilities/http-common";
import i18next from 'i18next';

const baseUrl = "/api/Certificates";

export function getCertificates(
    page: number,
    pageSize: number,
    direction: string,
    sortBy: string,
    referenceCode: string,
    dateFrom: Date | null | undefined,
    dateTo: Date | null | undefined,
    dateCreated: Date | null | undefined,
    requester: string,
    locations: string,
    status: string,
    department: string,
    organization: string,
    lastModificationTime?: Date | null | undefined,
    renewCreationDate?: Date | null | undefined,
    renewReferenceCode?: string,
    renewUrl: string = "paged",
    doExact: boolean = false
) {
    const url = `${baseUrl}/${renewUrl}`;
    const params = new URLSearchParams();

    if (page) params.append("page", page.toString());
    if (pageSize) params.append("pageSize", pageSize.toString());
    if (direction) params.append("direction", direction);
    if (sortBy) params.append("sortBy", sortBy);
    if (referenceCode) params.append("referenceCode", referenceCode);
    if (locations) params.append("locations", locations);
    if (requester) params.append("requester", requester);
    if (status) params.append("status", status);
    if (department && department !== '---') params.append("department", department);
    if (organization) params.append("organization", organization);
    if (renewReferenceCode) params.append("renewReferenceCode", renewReferenceCode);
    if (dateFrom != null) params.append("dateFrom", dateFrom.toString());
    if (dateTo != null) params.append("dateTo", dateTo.toString());
    if (dateCreated != null) params.append("dateCreated", dateCreated.toString());
    if (lastModificationTime != null) params.append("lastModificationTime", lastModificationTime.toString());
    if (renewCreationDate != null) params.append("renewCreationTime", renewCreationDate.toString());
    params.append("doExact", doExact.toString());

    return http
        .get(url, { params: params })
        .then((resp: AxiosResponse<CertificatesPagedResponse>) => {
            return resp.data;
        });
}

export function getCertificateWithDetails(id: string) {
    var url = `${baseUrl}/with-details?id=${id}`;

    return http.get(url).then((resp: AxiosResponse<CertificateWithDetails>) => {
        return resp.data;
    });
}

export function setOutOfService(id: string, date: Dayjs): Promise<any> {
    var url = `${baseUrl}/out-of-service?certificateId=${id}&outOfServiceDate=${date}`;

    return http.post(url).then((resp: AxiosResponse) => {
        return resp.data;
    });
}

export function updateCertificateRevoke(id: string, reason: string) {
    var url = `${baseUrl}/revoke`;


    var model: UpdateCertificateRevokedModel = {
        certificateId: id,
        reason: reason
    };
    return http.put(url, model).then((resp: AxiosResponse<CertificateWithDetails>) => {
        return resp.data;
    });
}

export function getByApplicationId(applicationId: string): Promise<CertificateWithDetails> {
    return http.get(`${baseUrl}/?applicationId=${applicationId}`)
        .then((res: AxiosResponse<CertificateWithDetails>) => res.data)
}


export async function exportDataAsCsv(dateTo: Dayjs, dateFrom: Dayjs, creationTimeFrom: Dayjs, creationTimeTo: Dayjs, extension: string) {
    const url = `${baseUrl}/csv`

    const params = new URLSearchParams();
    params.append("dateFrom", dateFrom.format('YYYY-MM-DD'));
    params.append("dateTo", dateTo.format('YYYY-MM-DD'));
    params.append("creationTimeFrom", creationTimeFrom.format('YYYY-MM-DD'));
    params.append("creationTimeTo", creationTimeTo.format('YYYY-MM-DD'));
    params.append("extension", extension);

    // Append the i18next language parameter
    const currentLanguage = i18next.language;
    params.append("culture", currentLanguage);

    return await http.get(url, { params: params, responseType: 'blob' as 'json' }).then((resp: AxiosResponse<Blob>) => {
        return resp.data;
    })
}

export function getSignedFile(id: string) {
    var url = `${baseUrl}/get-signed-file?id=${id}`;

    return http
        .get(url, { responseType: 'blob' as 'json' })
        .then((resp: AxiosResponse<Blob>) => {
            return resp.data;
        });
}

export async function checkPackageStatus(id: string) {
    var url = `${baseUrl}/check-package?id=${id}`;

    return http.get(url).then((resp: AxiosResponse<boolean>) => {
        return resp.data;
    });
}

export async function checkPackageApplicationStatus(id: string) {
    var url = `${baseUrl}/check-package-application?id=${id}`;

    return http.get(url).then((resp: AxiosResponse<boolean>) => {
        return resp.data;
    });
}

export async function resendCertificate(id: string) {
    var url = `${baseUrl}/send-certificate?id=${id}`;

    return http.get(url).then((resp: AxiosResponse) => {
        return resp.data;
    });
}

export async function validateRenewals(id: string[]): Promise<string[]> {
    var url = `${baseUrl}/validate-renewals`;

    return http.post(url, id).then((resp: AxiosResponse<string[]>) => {
        return resp.data;
    });
}

export async function renewCertificatesList(id: string[]): Promise<ApplicationDetailDTO[]> {
    var url = `${baseUrl}/renew-list`;

    return http.post(url, id).then((resp: AxiosResponse<ApplicationDetailDTO[]>) => {
        return resp.data;
    });
}

export async function renewCertificate(id: string, application: ApplicationWithDetails) {
    var url = `${baseUrl}/renew/${id}`;

    return http.post(url, application).then((resp: AxiosResponse) => {
        return resp.data;
    });
}

export async function postSignedFile(formData: FormData, certificateId: string, config: AxiosRequestConfig<FormData>) {
    var url = `${baseUrl}/post-signed-file/${certificateId}`;

    return http.post(url, formData, config).then((resp: AxiosResponse) => {
        return resp.data;
    });
}

export async function approveCertificate(id: string): Promise<CertificateWithDetails> {
    var url = `${baseUrl}/approve-cert/${id}`;

    return http.post(url).then((resp: AxiosResponse<CertificateWithDetails>) => {
        return resp.data;
    });
}

interface UpdateCertificateRevokedModel {
    certificateId: string;
    reason: string;
}
